/**
 * Shared Yimnak / YDIM navigation configuration for YimnakNavigationLauncher.
 * Single source — do not duplicate link lists in Header or Navbar.
 */

import { SYSTEMS_CONFIG } from "./systemUrls";

/** @typedef {'route' | 'external'} NavItemKind */

/**
 * @typedef {Object} NavItem
 * @property {string} id
 * @property {NavItemKind} kind
 * @property {string} labelKey
 * @property {string} [descriptionKey]
 * @property {string} [to] — internal react-router path
 * @property {string} [urlKey] — key in getSystemUrls() for external links
 * @property {string} [iconId]
 * @property {boolean} [requiresProtected]
 * @property {boolean} [requiresFirmMode]
 * @property {string} [analyticsEvent]
 * @property {string} [analyticsPage]
 */

/** @typedef {{ id: string; labelKey: string; items: NavItem[] }} NavGroup */

/** Legal workspace — internal Yimnak routes */
const WORKSPACE_ITEMS = /** @type {NavItem[]} */ ([
  {
    id: "disc",
    kind: "route",
    to: "/disc",
    labelKey: "nav.legalDiscussion",
    iconId: "message",
    requiresProtected: true,
    analyticsEvent: "disc_open",
    analyticsPage: "/disc",
  },
  {
    id: "chat",
    kind: "route",
    to: "/chat",
    labelKey: "nav.memorandums",
    iconId: "file",
    requiresProtected: true,
    analyticsEvent: "chat_open",
    analyticsPage: "/chat",
  },
  {
    id: "pleadings",
    kind: "route",
    to: "/pleadings",
    labelKey: "nav.pleadings",
    iconId: "gavel",
    requiresProtected: true,
    analyticsEvent: "pleadings_open",
    analyticsPage: "/pleadings",
  },
  {
    id: "tools",
    kind: "route",
    to: "/tools",
    labelKey: "nav.tools",
    iconId: "wrench",
  },
  {
    id: "firm",
    kind: "route",
    to: "/firm",
    labelKey: "nav.firmArchive",
    iconId: "archive",
    requiresFirmMode: true,
  },
]);

/** DimoY products — external systems (from SYSTEMS_CONFIG) */
const PRODUCT_ITEMS = /** @type {NavItem[]} */ (
  SYSTEMS_CONFIG.map((system) => ({
    id: system.id,
    kind: "external",
    urlKey: system.urlKey,
    labelKey: system.launcherLabelKey,
    descriptionKey: `appLauncher.${system.id === "legal" ? "legal" : system.id}Desc`,
    iconId: system.id,
  }))
);

/** General — home, services, contact */
const GENERAL_ITEMS = /** @type {NavItem[]} */ ([
  {
    id: "home",
    kind: "route",
    to: "/home",
    labelKey: "nav.home",
    iconId: "home",
    requiresProtected: true,
  },
  {
    id: "homePublic",
    kind: "route",
    to: "/",
    labelKey: "nav.home",
    iconId: "home",
    requiresPublicOnly: true,
  },
  {
    id: "services",
    kind: "route",
    to: "/services",
    labelKey: "ourServices",
    iconId: "services",
  },
  {
    id: "contact",
    kind: "route",
    to: "/contact",
    labelKey: "nav.contactUs",
    iconId: "contact",
    requiresProtected: true,
  },
]);

export const YIMNAK_NAV_GROUPS = /** @type {NavGroup[]} */ ([
  { id: "workspace", labelKey: "navGroups.workspace", items: WORKSPACE_ITEMS },
  { id: "products", labelKey: "navGroups.products", items: PRODUCT_ITEMS },
  { id: "general", labelKey: "navGroups.general", items: GENERAL_ITEMS },
]);

/**
 * Filter navigation items for the current session context.
 * @param {{ canAccessProtected: boolean; isFirmMode: boolean; variant: 'landing' | 'workspace' }} ctx
 */
export function getVisibleNavGroups(ctx) {
  const { canAccessProtected, isFirmMode, variant } = ctx;

  return YIMNAK_NAV_GROUPS.map((group) => {
    const items = group.items.filter((item) => {
      if (item.requiresProtected && !canAccessProtected) return false;
      if (item.requiresFirmMode && !isFirmMode) return false;
      if (item.requiresPublicOnly && canAccessProtected) return false;

      // Landing launcher: hide workspace unless authenticated; always show products
      if (variant === "landing" && group.id === "workspace" && !canAccessProtected) {
        return false;
      }

      // Deduplicate home/contact: show protected OR public variant, not both
      if (item.id === "homePublic" && canAccessProtected) return false;
      if (item.id === "home" && !canAccessProtected) return false;

      return true;
    });

    return { ...group, items };
  }).filter((group) => group.items.length > 0);
}

/** Match active route for internal links */
export function isNavItemActive(pathname, item) {
  if (item.kind !== "route" || !item.to) return false;
  if (item.to === "/") return pathname === "/";
  if (item.to === "/home") return pathname === "/home";
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
